/**
 * DictPurchases — история покупок карточки позиции (screen-21 J:214-248 cons /
 * J:300-329 res): хедер «История покупок (N)» + «+» cadd (прячется при
 * открытой форме), pform: Кол-во (int, только расходник, max 9999) + Сумма
 * (money, max 500000) + Медиа — РЕАЛЬНЫЙ выбор файлов (скрытый input
 * image/*, multiple — паттерн MediaTab) с превью выбранных; «Добавить» →
 * POST purchases + серия POST dict-media по файлу за запрос (Д8), строка
 * встаёт сверху без рефетча карточки (буфер полей не трогается). Дата — от
 * сервера (реальная, Д1). prow: дата DD.MM · кол-во · ₽сумма · чек-стопка
 * ×M → галерея photoov/pbig с реальными фото по подписанным URL (тап по
 * карточке — следующее, мимо — закрыть). Удаления медиа нет (эталон).
 * Недоехавшие по сети фото (DICT-5) не выбрасываются: живут в retry-стейте
 * с кнопкой «Повторить загрузку» (дозагрузка к СУЩЕСТВУЮЩЕЙ покупке —
 * POST dict-media это умеет); 413/415 в hint отличены от сетевых сбоев
 * (повтор >20 МБ/не-картинки бессмыслен — такие не ретраятся).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DictPurchase } from "../../api/dictTypes";
import { NB } from "../../hooks/useNumPadInput";
import { fmtNum } from "./PriceRow";
import { ActionPair } from "./QuestFormCells";
import type { DictPop } from "./DictBlocks";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import css from "./dicts.module.css";

const CamIco = ({ w = 14 }: { w?: number }) => (
  <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 8h4l2-3h4l2 3h4v12H4z" /><circle cx="12" cy="14" r="3.2" />
  </svg>
);
const RcptIco = ({ w = 13 }: { w?: number }) => (
  <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z" /><path d="M9 8h6M9 12h6" />
  </svg>
);

/** 'YYYY-MM-DD' → 'DD.MM' (формат дат эталона). */
const fmtDM = (iso: string): string => (iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : iso);
/** Подписи кадров галереи (KINDS эталона J:161, без «(mock)»). */
const KINDS = ["чек", "фото товара"];

interface PForm { qty: number; sum: number; files: File[]; urls: string[] }

type Media = { media_id: number; url: string };

export interface DictPurchasesProps {
  isCons: boolean;
  unit: string;
  purchases: DictPurchase[];
  addPurchase: (p: { qty?: number; sum: number }) => Promise<{ id: number; purchased_at: string } | { error: string }>;
  uploadMedia: (purchaseId: number, file: File) => Promise<Media | { fail: "big" | "type" | "denied" | "net" }>;
  onAdded: (p: DictPurchase) => void;
  /** Дозагрузка ретраем: медиа доехало к уже показанной покупке. */
  onMedia: (purchaseId: number, media: Media[]) => void;
  openPop: (cfg: DictPop) => void;
}

export function DictPurchases(p: DictPurchasesProps) {
  const [pf, setPf] = useState<PForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Файлы, не доехавшие по СЕТИ (413/415 не ретраятся) — ждут повтора. */
  const [retry, setRetry] = useState<{ pid: number; files: File[] } | null>(null);
  const [gal, setGal] = useState<{ p: DictPurchase; idx: number } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const madeUrls = useRef<string[]>([]);
  useEffect(() => () => { madeUrls.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const pick = (fl: FileList | null) => {
    const add = [...(fl ?? [])].filter((f) => f.type.startsWith("image/"));
    if (!add.length) return;
    const urls = add.map((f) => {
      const u = URL.createObjectURL(f);
      madeUrls.current.push(u);
      return u;
    });
    setPf((o) => (o ? { ...o, files: [...o.files, ...add], urls: [...o.urls, ...urls] } : o));
    if (fileRef.current) fileRef.current.value = "";
  };

  /** По файлу за запрос (контракт dict-media), последовательно. */
  const upl = async (pid: number, files: File[]) => {
    const media: Media[] = [];
    const failed: File[] = [];
    let big = 0;
    let bad = 0;
    let denied = 0;
    for (const f of files) {
      const m = await p.uploadMedia(pid, f);
      if ("media_id" in m) media.push(m);
      else if (m.fail === "big") big += 1;
      else if (m.fail === "type") bad += 1;
      else if (m.fail === "denied") denied += 1;
      else failed.push(f);
    }
    const parts = [
      failed.length ? `${failed.length} фото не загрузилось — проверьте связь` : "",
      big ? `${big} больше 20 МБ (сожмите)` : "",
      bad ? `${bad} не изображение` : "",
      denied ? `${denied} без прав на загрузку` : "",
    ].filter(Boolean);
    setRetry(failed.length ? { pid, files: failed } : null);
    setErr(parts.length ? `Покупка сохранена, но: ${parts.join("; ")}.` : null);
    return media;
  };

  const submit = async () => {
    if (!pf || busy || !(pf.sum > 0 && (!p.isCons || pf.qty > 0))) return;
    setBusy(true);
    setErr(null);
    const r = await p.addPurchase(p.isCons ? { qty: pf.qty, sum: pf.sum } : { sum: pf.sum });
    if ("error" in r) {
      setBusy(false);
      setErr(`Покупка не сохранилась: ${r.error}.`);
      return;
    }
    const media = await upl(r.id, pf.files);
    p.onAdded({
      id: r.id, qty: p.isCons ? pf.qty : 0, sum: pf.sum, purchased_at: r.purchased_at,
      staff_id: null, note: "", created_at: "", media,
    });
    setPf(null);
    setBusy(false);
  };

  const doRetry = async () => {
    if (!retry || busy) return;
    setBusy(true);
    setErr(null);
    const media = await upl(retry.pid, retry.files);
    if (media.length) p.onMedia(retry.pid, media);
    setBusy(false);
  };

  const numBtn = (label: string, text: string, pop: Omit<DictPop, "anchor" | "label">) => (
    <div className={css.ecell}>
      <span className={cfg.klbl}>{label}</span>
      <button type="button" className={`${cfg.teNumbtn} ${qcss.num}`}
        onClick={(e) => p.openPop({ anchor: e.currentTarget, label, ...pop })}>
        {text}
      </button>
    </div>
  );

  return (
    <>
      <div className={`${cfg.secRow} ${css.phead}`}>
        <span className={`${cfg.klbl} ${css.grow1}`}>История покупок ({p.purchases.length})</span>
        {pf ? null : (
          <button type="button" className={css.cadd} title="Добавить покупку"
            onClick={() => setPf({ qty: 0, sum: 0, files: [], urls: [] })}>
            +
          </button>
        )}
      </div>
      {pf ? (
        <div className={css.pform}>
          <div className={`${css.pfrow} ${p.isCons ? "" : css.two}`}>
            {p.isCons ? numBtn("Кол-во", `${pf.qty}${NB}${p.unit}`, {
              mode: "int", unit: p.unit, value: pf.qty, max: 9999,
              onCommit: (v) => setPf((o) => (o ? { ...o, qty: v || 0 } : o)),
            }) : null}
            {numBtn("Сумма", `₽${fmtNum(pf.sum)}`, {
              mode: "money", value: pf.sum, max: 500000,
              onCommit: (v) => setPf((o) => (o ? { ...o, sum: v || 0 } : o)),
            })}
            <div className={css.ecell}>
              <span className={cfg.klbl}>Медиа</span>
              <div className={css.mprev}>
                {pf.urls.map((u, i) => (
                  <span key={u} className={`${css.rcpt} ${css.pv}`}>
                    <img src={u} alt={KINDS[Math.min(i, 1)]} />
                  </span>
                ))}
                <button type="button" className={`${css.camb} ${pf.files.length ? css.on : ""}`}
                  title={p.isCons ? "Добавить фото (чеки, товар)" : "Добавить фото (чеки, ресурс)"}
                  onClick={() => fileRef.current?.click()}>
                  <CamIco />
                </button>
              </div>
            </div>
          </div>
          <ActionPair
            okLabel={busy ? "Добавляем…" : "Добавить"}
            okDisabled={busy || !(pf.sum > 0 && (!p.isCons || pf.qty > 0))}
            onOk={() => void submit()}
            onCancel={() => setPf(null)}
          />
        </div>
      ) : null}
      {err ? <div className={cfg.errHint}>{err}</div> : null}
      {retry ? (
        <button type="button" className={css.fclear} disabled={busy} onClick={() => void doRetry()}>
          {busy ? "Загружаем…" : `Повторить загрузку (${retry.files.length})`}
        </button>
      ) : null}
      {p.purchases.map((x) => (
        <div key={x.id} className={css.prow}>
          <span className={`${css.pd} ${qcss.num}`}>{fmtDM(x.purchased_at)}</span>
          {p.isCons ? <span className={`${css.pq} ${qcss.num}`}>{x.qty}{NB}{p.unit}</span> : null}
          <span className={`${css.ps} ${qcss.num}`}>₽{fmtNum(x.sum)}</span>
          {x.media.length ? (
            <button type="button" className={`${css.rcpt} ${css.stack}`} title="Открыть галерею"
              onClick={() => setGal({ p: x, idx: 0 })}>
              <RcptIco />
              {x.media.length > 1 ? <span className={`${css.stk} ${qcss.num}`}>×{x.media.length}</span> : null}
            </button>
          ) : (
            <span className={`${css.rcpt} ${css.no}`} title="без чека"><CamIco w={12} /></span>
          )}
        </div>
      ))}
      <input ref={fileRef} className={css.hiddenInp} type="file" accept="image/*" multiple
        onChange={(e) => pick(e.target.files)} />
      {gal ? createPortal(
        <div className={css.photoov}
          onPointerDown={(e) => {
            const inBig = e.target instanceof Element && e.target.closest(`.${css.pbig}`);
            const n = gal.p.media.length;
            if (inBig && n > 1) setGal({ p: gal.p, idx: (gal.idx + 1) % n });
            else setGal(null);
          }}>
          <div className={css.pbig}>
            <div className={`${css.pbigH} ${qcss.num}`}>
              {fmtDM(gal.p.purchased_at)} · ₽{fmtNum(gal.p.sum)} · {gal.idx + 1}/{gal.p.media.length}
            </div>
            <div className={css.pbigImg}>
              <img src={gal.p.media[gal.idx]?.url} alt={KINDS[Math.min(gal.idx, 1)]} />
            </div>
            <div className={css.pbigS}>
              {KINDS[Math.min(gal.idx, 1)]}{gal.p.media.length > 1 ? " · тап — следующее" : ""}
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
